/**
 * Iter 67fa — Vendor "Booking widget" panel.
 *
 * Lets the vendor:
 *  - Pick / edit their public_slug (URL-safe handle for `/v/{slug}`).
 *  - Pick a brand_color (hex) that themes both the micro-site and embed.
 *  - Copy the live micro-site URL.
 *  - Copy a ready-to-paste iframe snippet for their own website.
 *  - Preview the embed inline.
 *
 * Hits:
 *   GET  /api/vendor/public-slug-status — current + suggested slug
 *   PATCH /api/users/me/business         — saves public_slug + brand_color
 */
import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import VendorWidgetAttributionMini from "@/components/vendor/VendorWidgetAttributionMini";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Globe2, Copy, ExternalLink, Loader2, Wand2, CheckCircle2, Paintbrush, QrCode, Download, Image as ImageIcon, Share2, MessageSquare, Send, Twitter, Facebook, Mail } from "lucide-react";

const FALLBACK_ORIGIN = "https://rentalcar.al";

// Iter 67fe — one-tap share targets.
// Note on Instagram: it has no public URL-share API, so the standard pattern is
// copy-to-clipboard + open the app deep-link. We do exactly that.
function ShareBar({ slug, micrositeUrl, businessName }) {
  const text = `Book a car with ${businessName} — ${micrositeUrl}`;
  const encoded = encodeURIComponent(text);
  const encodedUrl = encodeURIComponent(micrositeUrl);
  const targets = [
    { id: "whatsapp", label: "WhatsApp", Icon: MessageSquare, url: `https://wa.me/?text=${encoded}`,
      cls: "bg-green-50 text-green-700 border-green-200 hover:bg-green-100" },
    { id: "telegram", label: "Telegram", Icon: Send, url: `https://t.me/share/url?url=${encodedUrl}&text=${encoded}`,
      cls: "bg-sky-50 text-sky-700 border-sky-200 hover:bg-sky-100" },
    { id: "facebook", label: "Facebook", Icon: Facebook, url: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`,
      cls: "bg-blue-50 text-blue-700 border-blue-200 hover:bg-blue-100" },
    { id: "x", label: "X / Twitter", Icon: Twitter, url: `https://x.com/intent/post?url=${encodedUrl}&text=${encodeURIComponent("Book a car with " + businessName)}`,
      cls: "bg-neutral-50 text-neutral-800 border-neutral-200 hover:bg-neutral-100" },
    { id: "email", label: "Email", Icon: Mail, url: `mailto:?subject=${encodeURIComponent("Book a car with " + businessName)}&body=${encoded}`,
      cls: "bg-amber-50 text-amber-800 border-amber-200 hover:bg-amber-100" },
  ];

  async function shareNativeOrInstagram() {
    // Prefer the Web Share API on mobile (renders the system sheet with IG / WhatsApp / etc).
    if (navigator.share) {
      try {
        await navigator.share({ title: businessName, text, url: micrositeUrl });
        return;
      } catch (_e) { /* user cancelled — fall through to clipboard */ }
    }
    // Desktop fallback: copy link + deep-link into Instagram (will route to the
    // app on mobile, or the IG website on desktop where the user can paste).
    try {
      await navigator.clipboard.writeText(micrositeUrl);
      toast.success("Link copied — paste it into your Instagram bio or story");
    } catch {
      toast.info(`Copy this link to share on Instagram: ${micrositeUrl}`);
    }
    window.open("https://www.instagram.com/", "_blank", "noreferrer");
  }

  return (
    <div className="space-y-2" data-testid="widget-share-bar">
      <div className="text-xs uppercase tracking-widest text-neutral-500 font-bold flex items-center gap-1.5">
        <Share2 className="h-3 w-3" /> Share to
      </div>
      <div className="flex flex-wrap gap-2">
        {targets.map(({ id, label, Icon, url, cls }) => (
          <a
            key={id}
            href={url}
            target="_blank"
            rel="noreferrer"
            className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-shadow hover:shadow-sm ${cls}`}
            data-testid={`widget-share-${id}`}
          >
            <Icon className="h-3.5 w-3.5" />
            {label}
          </a>
        ))}
        <button
          type="button"
          onClick={shareNativeOrInstagram}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-shadow hover:shadow-sm bg-pink-50 text-pink-700 border-pink-200 hover:bg-pink-100"
          data-testid="widget-share-instagram"
          title="Web Share on mobile · copy link + open Instagram on desktop"
        >
          {/* Instagram glyph — small inline SVG to avoid pulling a new icon set */}
          <svg className="h-3.5 w-3.5" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M12 2.2c3.2 0 3.58.01 4.85.07 1.17.05 1.8.25 2.23.41.56.22.96.48 1.38.9.42.42.68.82.9 1.38.16.42.36 1.06.41 2.22.06 1.27.07 1.65.07 4.85s-.01 3.58-.07 4.85c-.05 1.17-.25 1.8-.41 2.23a3.7 3.7 0 01-.9 1.38c-.42.42-.82.68-1.38.9-.42.16-1.06.36-2.23.41-1.27.06-1.65.07-4.85.07s-3.58-.01-4.85-.07c-1.17-.05-1.8-.25-2.22-.41a3.7 3.7 0 01-1.38-.9 3.7 3.7 0 01-.9-1.38c-.17-.42-.36-1.06-.42-2.23-.06-1.27-.07-1.65-.07-4.85s.01-3.58.07-4.85c.06-1.17.25-1.8.42-2.22.22-.56.48-.96.9-1.38.42-.42.82-.68 1.38-.9.42-.16 1.05-.36 2.22-.41C8.42 2.21 8.8 2.2 12 2.2zm0 5.8a4 4 0 100 8 4 4 0 000-8zm5.4-.2a.9.9 0 110-1.8.9.9 0 010 1.8zM12 13.6a1.6 1.6 0 110-3.2 1.6 1.6 0 010 3.2z"/>
          </svg>
          Instagram
        </button>
      </div>
    </div>
  );
}

function _origin() {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return window.location.origin;
  }
  return FALLBACK_ORIGIN;
}

function _copy(value, label) {
  if (!value) return;
  try {
    navigator.clipboard.writeText(value);
    toast.success(`${label || "Copied"} to clipboard`);
  } catch {
    toast.error("Could not copy — please copy manually");
  }
}

export default function VendorBookingWidgetPanel() {
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [slugInput, setSlugInput] = useState("");
  const [brandColor, setBrandColor] = useState("#1D4ED8");
  const [qrPreviewUrl, setQrPreviewUrl] = useState(null);
  const [qrLoading, setQrLoading] = useState(false);
  const [qrWithLogo, setQrWithLogo] = useState(true);

  async function refresh() {
    setLoading(true);
    try {
      const res = await api.get("/vendor/public-slug-status");
      setStatus(res.data);
      setSlugInput(res.data.public_slug || "");
      setBrandColor(res.data.brand_color || "#1D4ED8");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, []);

  async function save() {
    setSaving(true);
    try {
      const payload = {};
      const next_slug = (slugInput || "").trim().toLowerCase();
      if (next_slug && next_slug !== (status?.public_slug || "")) payload.public_slug = next_slug;
      if (brandColor && brandColor !== (status?.brand_color || "")) payload.brand_color = brandColor;
      if (Object.keys(payload).length === 0) {
        toast.info("Nothing to save");
        setSaving(false);
        return;
      }
      await api.patch("/users/me/business", payload);
      toast.success("Booking widget settings saved");
      await refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  }

  async function useSuggested() {
    if (!status?.suggested_slug) return;
    setSlugInput(status.suggested_slug);
  }

  // Iter 67fb — load the QR code as a blob so we can preview it inline AND
  // hand the same Blob to download/print. Refreshes when the saved slug or
  // brand color changes (so the QR always reflects what's saved server-side).
  useEffect(() => {
    let active = true;
    let createdUrl = null;
    if (!status?.public_slug) {
      setQrPreviewUrl(null);
      return undefined;
    }
    setQrLoading(true);
    (async () => {
      try {
        const qs = qrWithLogo ? "?with_logo=true" : "";
        const res = await api.get(`/vendor/booking-widget/qr-code${qs}`, { responseType: "blob" });
        if (!active) return;
        createdUrl = URL.createObjectURL(res.data);
        setQrPreviewUrl(createdUrl);
      } catch (e) {
        toast.error(formatApiError(e));
      } finally {
        if (active) setQrLoading(false);
      }
    })();
    return () => {
      active = false;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [status?.public_slug, status?.brand_color, qrWithLogo]);

  async function downloadQr(fmt) {
    try {
      const params = new URLSearchParams({ fmt });
      // Logo overlay only applies to PNG (SVG would need a different code path
      // and printers don't need it for vector workflows anyway).
      if (fmt === "png" && qrWithLogo) params.set("with_logo", "true");
      const res = await api.get(`/vendor/booking-widget/qr-code?${params.toString()}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `rentalcar-qr-${status?.public_slug || "widget"}.${fmt}`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (e) {
      toast.error(formatApiError(e));
    }
  }

  function printQr() {
    if (!qrPreviewUrl || !status?.public_slug) return;
    // Open a small printable card — branded heading + QR + URL beneath. Works
    // on standard A6 sticker labels (windscreen / shop window / business cards).
    const url = `${_origin()}/v/${status.public_slug}`;
    const w = window.open("", "_blank", "width=420,height=620");
    if (!w) {
      toast.error("Pop-up blocked — please allow pop-ups to print");
      return;
    }
    w.document.write(`<!doctype html><html><head><title>Booking widget QR</title>
      <style>
        body{font-family:-apple-system,BlinkMacSystemFont,Segoe UI,sans-serif;text-align:center;padding:32px;margin:0;color:#111;background:#fff}
        h1{font-size:18px;font-weight:800;margin:0 0 4px;color:${status.brand_color || "#1D4ED8"}}
        h2{font-size:14px;font-weight:600;color:#444;margin:0 0 24px}
        img{width:240px;height:240px;border:1px solid #eee;border-radius:12px;padding:8px;background:#fff}
        code{display:block;font-size:11px;color:#666;margin-top:14px;word-break:break-all}
        p{font-size:12px;color:#666;margin-top:14px}
        @media print{ body{padding:8mm} }
      </style></head><body>
      <h1>Scan to book</h1>
      <h2>Powered by RentalCar.al</h2>
      <img src="${qrPreviewUrl}" alt="QR code"/>
      <code>${url}</code>
      <p>Point your phone camera at the code.</p>
      <script>setTimeout(()=>{window.print();},200);<\/script>
      </body></html>`);
    w.document.close();
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-neutral-500" data-testid="widget-loading">
        <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading booking-widget settings…
      </div>
    );
  }

  const origin = _origin();
  const slug = status?.public_slug;
  const micrositeUrl = slug ? `${origin}/v/${slug}` : null;
  const embedUrl = slug ? `${origin}/embed/v/${slug}` : null;
  const iframeSnippet = embedUrl
    ? `<iframe src="${embedUrl}" width="100%" height="900" frameborder="0" style="border:0;border-radius:12px;" title="RentalCar.al booking widget"></iframe>`
    : "";

  return (
    <div className="space-y-6" data-testid="vendor-booking-widget-panel">
      {/* Intro */}
      <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50 via-violet-50/30 to-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-indigo-900">
            <Globe2 className="h-5 w-5" />
            Embeddable booking widget
          </CardTitle>
          <CardDescription className="text-indigo-800/80">
            Put a self-service booking page on your own website. We host the booking flow, the payment, the agreement —
            you just paste 1 line of code. Bookings made through your widget count toward your dashboard exactly the
            same as organic bookings.
          </CardDescription>
        </CardHeader>
      </Card>

      {/* Iter 67fd — vendor-side attribution mini panel. Self-hides until a
          slug is set, so it doesn't add noise during onboarding. */}
      <VendorWidgetAttributionMini />

      {/* Step 1 — Slug + brand */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Step 1 · Your public link</CardTitle>
          <CardDescription>
            Pick a short, memorable handle. This becomes your micro-site URL:
            <code className="px-1 mx-1 rounded bg-neutral-100 text-neutral-700">/v/your-handle</code>
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-5">
          <div>
            <Label>Public slug</Label>
            <div className="flex items-stretch gap-2 mt-1">
              <div className="flex items-stretch flex-1 rounded-md border border-input overflow-hidden bg-white">
                <span className="flex items-center px-3 text-xs text-neutral-500 bg-neutral-50 border-r border-input font-mono">
                  {origin}/v/
                </span>
                <Input
                  className="border-0 flex-1 rounded-none font-mono text-sm"
                  value={slugInput}
                  onChange={(e) => setSlugInput(e.target.value.toLowerCase().replace(/[^a-z0-9-]+/g, "-"))}
                  placeholder="my-shop"
                  maxLength={40}
                  data-testid="widget-slug-input"
                />
              </div>
              {status?.suggested_slug && status?.suggested_available && slugInput !== status.suggested_slug && (
                <Button variant="outline" type="button" onClick={useSuggested} data-testid="widget-use-suggested">
                  <Wand2 className="h-4 w-4 mr-1.5" /> {status.suggested_slug}
                </Button>
              )}
            </div>
            <div className="text-xs text-neutral-500 mt-1.5">
              Lowercase letters, numbers, and hyphens only. 3–40 characters.
            </div>
          </div>

          <div>
            <Label className="flex items-center gap-1.5"><Paintbrush className="h-3.5 w-3.5" /> Brand color</Label>
            <div className="flex items-center gap-3 mt-1">
              <input
                type="color"
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value.toUpperCase())}
                className="h-10 w-14 rounded border cursor-pointer"
                data-testid="widget-brand-color-input"
                aria-label="Brand color"
              />
              <Input
                value={brandColor}
                onChange={(e) => setBrandColor(e.target.value)}
                className="font-mono max-w-[140px]"
                placeholder="#1D4ED8"
                maxLength={7}
                data-testid="widget-brand-color-hex"
              />
              <span
                className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-white text-xs font-semibold shadow-sm"
                style={{ backgroundColor: brandColor }}
                data-testid="widget-brand-preview-pill"
              >
                <CheckCircle2 className="h-3.5 w-3.5" /> Preview
              </span>
            </div>
          </div>

          <Button onClick={save} disabled={saving} data-testid="widget-save">
            {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
            Save settings
          </Button>
        </CardContent>
      </Card>

      {/* Step 2 — Share links */}
      {slug && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Step 2 · Share your micro-site</CardTitle>
            <CardDescription>
              Link to this from your Instagram bio, Google Business profile, or printed flyers.
              Renters land on a branded page with all your active cars.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-stretch gap-2">
              <code className="flex-1 p-2.5 rounded-md bg-neutral-50 border font-mono text-xs break-all" data-testid="widget-microsite-url">
                {micrositeUrl}
              </code>
              <Button variant="outline" size="sm" onClick={() => _copy(micrositeUrl, "Link")} data-testid="widget-copy-microsite">
                <Copy className="h-4 w-4 mr-1.5" /> Copy
              </Button>
              <a href={micrositeUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="widget-open-microsite">
                  <ExternalLink className="h-4 w-4 mr-1.5" /> Open
                </Button>
              </a>
            </div>

            {/* Iter 67fe — one-tap share buttons. WhatsApp is the dominant Balkan
                channel; Instagram doesn't have a direct URL-share API so we copy
                to clipboard + deep-link into the app. Telegram / X / FB / Email
                round out the social-sharing surface. */}
            <ShareBar slug={slug} micrositeUrl={micrositeUrl} businessName={status?.business_name || "Our cars"} />
          </CardContent>
        </Card>
      )}

      {/* Step 2.5 — Printable QR (Iter 67fb) */}
      {slug && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <QrCode className="h-4 w-4" /> Printable QR code
            </CardTitle>
            <CardDescription>
              Print this and stick it on the windscreen, shop window, or business cards.
              Scans land straight on your branded booking page — perfect for walk-up renters
              and offline marketing.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex flex-col sm:flex-row gap-6 items-start">
              <div
                className="rounded-xl border bg-white shadow-sm p-3 flex items-center justify-center w-[200px] h-[200px] flex-shrink-0"
                data-testid="widget-qr-preview-wrap"
                style={{ borderColor: brandColor }}
              >
                {qrLoading ? (
                  <Loader2 className="h-6 w-6 animate-spin text-neutral-400" />
                ) : qrPreviewUrl ? (
                  <img
                    src={qrPreviewUrl}
                    alt={`QR for ${slug}`}
                    className="w-full h-full object-contain"
                    data-testid="widget-qr-preview-img"
                  />
                ) : (
                  <span className="text-xs text-neutral-400">QR not available</span>
                )}
              </div>
              <div className="flex-1 space-y-3">
                <div className="text-sm text-neutral-700">
                  Encoded URL:
                  <code className="block mt-1 p-2 rounded bg-neutral-50 border font-mono text-[11px] break-all" data-testid="widget-qr-encoded-url">
                    {micrositeUrl}
                  </code>
                </div>
                <div className="text-xs text-neutral-500">
                  The QR is auto-themed with your brand color and refreshes whenever you
                  change the slug above. Use SVG for crisp prints at any size; PNG works
                  best for casual sharing.
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" size="sm" onClick={() => downloadQr("png")} data-testid="widget-qr-download-png">
                    <Download className="h-4 w-4 mr-1.5" /> Download PNG
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => downloadQr("svg")} data-testid="widget-qr-download-svg">
                    <Download className="h-4 w-4 mr-1.5" /> Download SVG
                  </Button>
                  <Button variant="outline" size="sm" onClick={printQr} disabled={!qrPreviewUrl} data-testid="widget-qr-print">
                    <QrCode className="h-4 w-4 mr-1.5" /> Print card
                  </Button>
                </div>
                {/* Logo overlay toggle (Iter 67fe) */}
                <label className="flex items-center gap-2 text-xs text-neutral-700 cursor-pointer" data-testid="widget-qr-logo-toggle-wrap">
                  <input
                    type="checkbox"
                    checked={qrWithLogo}
                    onChange={(e) => setQrWithLogo(e.target.checked)}
                    className="h-3.5 w-3.5 rounded border-neutral-300 cursor-pointer"
                    data-testid="widget-qr-logo-toggle"
                  />
                  <ImageIcon className="h-3.5 w-3.5 text-neutral-500" />
                  Centre my logo on the PNG (error-correction H keeps scans reliable)
                </label>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3 — Embed code */}
      {slug && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Step 3 · Embed on your own website</CardTitle>
            <CardDescription>
              Paste this iframe snippet into your existing site (WordPress, Wix, Shopify, custom HTML — all work).
              Bookings happen inline; customers never leave your page.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="rounded-md bg-neutral-900 text-neutral-100 font-mono text-xs p-3 leading-relaxed overflow-x-auto" data-testid="widget-iframe-snippet">
              {iframeSnippet}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" onClick={() => _copy(iframeSnippet, "Embed code")} data-testid="widget-copy-embed">
                <Copy className="h-4 w-4 mr-1.5" /> Copy embed code
              </Button>
              <a href={embedUrl} target="_blank" rel="noreferrer">
                <Button variant="outline" size="sm" data-testid="widget-open-embed">
                  <ExternalLink className="h-4 w-4 mr-1.5" /> Preview embed
                </Button>
              </a>
            </div>

            {/* Live inline preview */}
            <div className="rounded-lg overflow-hidden border bg-white" data-testid="widget-iframe-preview-wrap">
              <div className="text-[11px] uppercase tracking-widest px-3 py-1.5 bg-neutral-100 text-neutral-500 border-b font-semibold">
                Live preview
              </div>
              <iframe
                src={embedUrl}
                title="Booking widget preview"
                width="100%"
                height="600"
                style={{ border: 0 }}
                data-testid="widget-iframe-preview"
              />
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}
